import { useEffect, useCallback, useMemo, useRef, type Ref } from 'react';
import { useSelector } from 'react-redux';
import {
  getInspectionId,
  getFocusedDifference,
  getAllAnnotationsLoaded,
  getFocusedMatchIndex,
  sortDifferences,
  getDifferenceSortOption,
  getSelectedModule,
  getSelectedCustomDictionaryIDs,
  getInspectionOriginalOrgId,
  getUnfilteredDifferences,
  getSubgroupedDifferences,
} from 'store';
import { groupDifferencesByGroupOrWord } from 'store/request/inspections/groupingUtils';
import type { DifferenceNumbering } from '@verify/shared-utils';
import { useDifferenceListStore, rebuildDifferenceList } from 'zstore/differenceListStore';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import { getStyleVariables } from 'styles/vars';
import { Grid } from '@mui/material';
import DifferenceElement from './DifferenceElement';
import { useTheme } from '@emotion/react';
import { useInspectionCustomStatuses } from 'store/queries/organizations/useCustomStatuses';
import { useCustomDictionaries } from 'store/queries/dictionaries/customDictionaries';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { Difference } from 'store/request/differences/types';
import { useUserStateStore } from 'zstore/userStateStore';
import { getOrganizationId } from 'utils/auth';
import { useDifferenceListInteraction } from './useDifferenceListInteraction';
import {
  DifferenceListLoading,
  DifferenceListZeroResultsHeader,
  useDifferenceListSharedStyles,
} from './DifferenceListShared';

export interface ListOfDifferencesProps {
  multipleSelectionMode?: boolean;
  selectedDifferenceIds?: string[];
  onDifferenceSelection?: (difference: Difference, checked: boolean) => void;
}

const ListOfDifferences = ({
  multipleSelectionMode = false,
  selectedDifferenceIds = [],
  onDifferenceSelection = () => {},
}: ListOfDifferencesProps) => {
  const { classes } = useDifferenceListSharedStyles();
  const theme = useTheme();
  const styleVariables = getStyleVariables(theme);
  const sortOption = useSelector(getDifferenceSortOption);
  const inspectionId = useSelector(getInspectionId);
  const {
    data: inspectionCustomStatuses,
    isLoading: customStatusesLoading,
    refetch: refetchStatuses,
  } = useInspectionCustomStatuses(inspectionId);

  const originalOrgId = useSelector(getInspectionOriginalOrgId);
  const { user } = useUserStateStore();
  const userOrgId = getOrganizationId(user);
  const isDifferentOrg = originalOrgId && originalOrgId !== userOrgId;

  const customDictionaryIDs = useSelector(getSelectedCustomDictionaryIDs);
  const additionalDictionaryIds = isDifferentOrg ? customDictionaryIDs : undefined;
  const { customDictionaryMap } = useCustomDictionaries({ getAll: true, additionalDictionaryIds });
  let canSuggestToCustomDictionary = true;
  if (additionalDictionaryIds && additionalDictionaryIds?.length > 0) {
    const customDictionaryId = customDictionaryIDs?.[0];
    const customDictionary = customDictionaryMap.get(customDictionaryId);
    canSuggestToCustomDictionary = customDictionary?.orgId === userOrgId;
  }

  const selectedModule = useSelector(getSelectedModule);
  const unfiltered = useSelector(getUnfilteredDifferences);
  const subgrouped = useSelector(getSubgroupedDifferences);
  const differenceCards = useDifferenceListStore((s) => s.differenceCards);

  // Status titles live in React Query (not Redux); rebuild when they load/change.
  useEffect(() => {
    if (inspectionCustomStatuses) {
      rebuildDifferenceList(subgrouped, unfiltered, selectedModule, sortOption, inspectionCustomStatuses);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspectionCustomStatuses]);

  // All tab: legacy sort. Per-module: pre-computed cards from the store.
  // TODO: remove the All-tab branch when showAllResultsTab flag is retired.
  const differenceList = useMemo((): Array<Difference & Partial<DifferenceNumbering>> => {
    if (selectedModule === 'All') {
      return sortDifferences(groupDifferencesByGroupOrWord(unfiltered), sortOption, inspectionCustomStatuses);
    }
    return differenceCards;
  }, [selectedModule, differenceCards, unfiltered, inspectionCustomStatuses, sortOption]);

  const focusedDifference = useSelector(getFocusedDifference);
  const focusedMatchIndex = useSelector(getFocusedMatchIndex);
  const currentDifferenceId = focusedDifference?.id;
  const currentDifferenceGroupId = focusedDifference?.groupId;
  const annotationsLoaded = useSelector(getAllAnnotationsLoaded);

  const getDifferenceIndex = useCallback(() => {
    if (!currentDifferenceId && !currentDifferenceGroupId) {
      return differenceList.findIndex((difference) => difference.matchIndex === focusedMatchIndex);
    }
    return differenceList.findIndex(
      (difference) =>
        difference.id === currentDifferenceId ||
        (difference.groupId && difference.groupId === currentDifferenceGroupId),
    );
  }, [currentDifferenceId, currentDifferenceGroupId, differenceList, focusedMatchIndex]);

  // Hotkeys pass numberOfDifference + 1; on this flat list that value is the list index.
  const resolveHotkeyTargetToListIndex = useCallback((hotkeyTarget: number) => hotkeyTarget, []);

  const {
    virtuoso,
    currentItemIndex,
    clickSelect,
    setClickSelect,
    openComments,
    onClickSelect,
    getCardRef,
    updateCommentOpen,
    handleOnStopKeyboardNavigation,
    scrollerRef,
    enableKeyboardNavigation,
  } = useDifferenceListInteraction({
    itemCount: differenceList.length,
    getFocusedListIndex: getDifferenceIndex,
    resolveHotkeyTargetToListIndex,
    initialCommentIds: differenceList.map((d) => d.id),
  });

  const previousIndexRef = useRef<number>(-1);

  // Keep selection in view after a re-sort (e.g. deletion marker move).
  useDeepCompareEffect(() => {
    if (!currentDifferenceId || clickSelect) return;

    const index = getDifferenceIndex();
    const previousIndex = previousIndexRef.current;

    if (previousIndex !== -1 && previousIndex !== index && index > -1 && virtuoso.current) {
      setTimeout(() => {
        if (virtuoso.current && getDifferenceIndex() > -1) {
          virtuoso.current.scrollToIndex({ index: getDifferenceIndex(), align: 'start', behavior: 'smooth' });
        }
      }, 100);
    }

    previousIndexRef.current = index;
    /* eslint-disable-next-line */
  }, [differenceList, currentDifferenceId, clickSelect]);

  useEffect(() => {
    const index = getDifferenceIndex();

    if (virtuoso.current !== null && index > -1) {
      if (!clickSelect) {
        // Annotation click → jump to card.
        requestAnimationFrame(() => {
          if (virtuoso.current) {
            virtuoso.current.scrollToIndex({ index, align: 'start', behavior: 'auto' });
          }
        });
      } else {
        // List click → ensure partial card is fully visible.
        virtuoso.current.scrollIntoView({ index, behavior: 'smooth' });
      }
    }

    previousIndexRef.current = index;
    setClickSelect(false);
    /* eslint-disable-next-line */
  }, [currentDifferenceId, currentDifferenceGroupId, focusedMatchIndex, differenceList.length]);

  const hasZeroResults = differenceList.length === 0;
  const selectedDifferenceIdSet = useMemo(() => new Set(selectedDifferenceIds), [selectedDifferenceIds]);

  useEffect(() => {
    if (virtuoso.current && differenceList.length > 0) {
      const shouldScrollToTop = getDifferenceIndex() === -1;
      if (shouldScrollToTop) {
        virtuoso.current.scrollToIndex({ index: 0, align: 'start', behavior: 'auto' });
      }
    }
  }, [selectedModule]);

  return !customStatusesLoading && annotationsLoaded ? (
    <Virtuoso
      ref={virtuoso as Ref<VirtuosoHandle>}
      scrollerRef={enableKeyboardNavigation ? scrollerRef : (_: HTMLElement | Window | null) => {}}
      context={{ currentItemIndex }}
      style={{
        width: styleVariables.leftPanel.width,
        height: '100%',
        backgroundColor: '#0000003D',
        scrollbarGutter: 'auto',
      }}
      className={classes.scrollBar}
      totalCount={differenceList.length}
      components={{
        Header: () => {
          if (hasZeroResults) {
            return <DifferenceListZeroResultsHeader className={classes.zeroResults} />;
          }
          return <Grid style={{ marginTop: '8px' }} />;
        },
      }}
      itemContent={(index, _, { currentItemIndex: keyboardIndex }) => {
        const isKeyboardFocused = keyboardIndex === index;
        const difference = differenceList[index];
        const isSelected = selectedDifferenceIdSet.has(difference.id);
        const typedDiff = difference as Difference & Partial<DifferenceNumbering>;
        // Per-module: groupIndex (1-based). All tab: array index.
        const cardIndex = typedDiff.groupIndex != null ? typedDiff.groupIndex - 1 : index;

        return (
          <Grid className={classes.differenceContainer}>
            <DifferenceElement
              ref={getCardRef(index)}
              key={difference.id}
              numberOfDifference={cardIndex}
              groupIndex={typedDiff.groupIndex}
              listIndex={index}
              onClickSelect={onClickSelect}
              difference={difference}
              nextDiffId={differenceList[index + 1]?.id}
              isSelected={
                currentDifferenceId === difference.id ||
                (Boolean(currentDifferenceGroupId) && currentDifferenceGroupId === difference.groupId)
              }
              isKeyboardFocused={isKeyboardFocused}
              updateCommentOpen={updateCommentOpen}
              isCommentOpen={openComments[difference.id]}
              handleOnStopKeyboardNavigation={handleOnStopKeyboardNavigation}
              multipleSelectionMode={multipleSelectionMode}
              isCheckboxSelected={isSelected}
              onCheckboxChange={onDifferenceSelection}
              customStatuses={inspectionCustomStatuses}
              canSuggestToCustomDictionary={canSuggestToCustomDictionary}
              refetchStatuses={refetchStatuses}
            />
          </Grid>
        );
      }}
    />
  ) : (
    <DifferenceListLoading className={classes.containerPadding} />
  );
};

export default ListOfDifferences;
